// theirs
import React from 'react';
import * as H from 'history';

// ours
import LiteralsService from '../../../services/literals/LiteralsService';
import Typography from '../../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import {
    Anchor,
    IconRight,
    IconBank,
    IconLineGraph,
    IconHome,
    IconPayOverTime,
    GlyphLinkOut
} from '@americanexpress/dls-react';
import Drawer from '../../../components/drawer/Drawer';

// styles and icons
import styles from './AddAccountsDrawer.module.scss';
import NavigationService from 'src/services/navigation.service';
import Callout from 'src/components/atomic/atoms/callout/Callout';
import { DLSSize } from 'src/types/dls-size.enum';
import ConnectAccountsButton from 'src/components/atomic/atoms/connectAccountsButton/ConnectAccountsButton';
import colors from 'src/styles/colors.module.scss';

interface AddAccountsProps {
    handleBack: () => void;
    history: H.History;
}

export default function AddAccountsDrawer({ handleBack, history }: AddAccountsProps): JSX.Element {
    return (
        <Drawer
            drawerProps={{ history }}
            handleBack={handleBack}
            title={LiteralsService.getLiteral('addAccountDrawer.title')}
            withBackButton
            className={styles.addAccountsDrawer}
        >
            <Typography variant={TypographyVariants.BODY}>
                {LiteralsService.getLiteral('addAccountDrawer.connectAccounts')}
            </Typography>
            <Callout withButton={false}>
                <Typography variant={TypographyVariants.HEADING_XS} className={colors.textStrong}>
                    {LiteralsService.getLiteral('addAccountDrawer.addNonAmericanExpressAccounts')}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT_BOLD}>
                    {LiteralsService.getLiteral('addAccountDrawer.whyShouldUouConnect')}
                </Typography>
                <ul>
                    <li>
                        <Typography variant={TypographyVariants.SUBTEXT}>
                            {LiteralsService.getLiteral('addAccountDrawer.makeYourPlanMoreAccurate')}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.SUBTEXT}>
                            {LiteralsService.getLiteral('addAccountDrawer.gainMoreControl')}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.SUBTEXT}>
                            {LiteralsService.getLiteral('addAccountDrawer.yourBalancesStay')}
                        </Typography>
                    </li>
                    <li>
                        <Typography variant={TypographyVariants.SUBTEXT}>
                            {LiteralsService.getLiteral('addAccountDrawer.seeIfYoureOverspending')}
                        </Typography>
                    </li>
                </ul>
                <Typography variant={TypographyVariants.BODY}>
                    <Anchor>{LiteralsService.getLiteral('addAccountDrawer.readMoreAboutConnectingAccounts')}</Anchor>
                    <GlyphLinkOut className={styles.linkOut} size={DLSSize.SMALL} />
                </Typography>
                <ConnectAccountsButton />
                <Typography variant={TypographyVariants.SUBTEXT} className={styles.borderTop}>
                    {LiteralsService.getLiteral('addAccountDrawer.termsAndConditions[0]')}
                    <Anchor>{LiteralsService.getLiteral('addAccountDrawer.termsAndConditions[1]')}</Anchor>
                    {LiteralsService.getLiteral('addAccountDrawer.termsAndConditions[2]')}
                </Typography>
            </Callout>

            <div className={styles.addAccountButtonsContainer} data-cy="addAccountsButtonsContainer">
                <button
                    className={[styles.addAccountButton, styles.investmentButton].join(' ')}
                    onClick={() => {
                        void NavigationService.goTo(history, '/add-accounts/cash-and-banking');
                    }}
                >
                    <IconBank size="md" />
                    <div className={styles.textColumn}>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('addAccountDrawer.cashAndBanking.buttonTitle')}
                        </Typography>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('addAccountDrawer.cashAndBanking.buttonSubtitle')}
                        </Typography>
                    </div>
                    <IconRight />
                </button>
                <button
                    className={[styles.addAccountButton, styles.investmentButton].join(' ')}
                    onClick={() => {
                        void NavigationService.goTo(history, '/add-accounts/investments-and-retirement');
                    }}
                >
                    <IconLineGraph size="md" />
                    <div className={styles.textColumn}>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('addAccountDrawer.investmentsAndRetirement.buttonTitle')}
                        </Typography>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('addAccountDrawer.investmentsAndRetirement.buttonSubtitle')}
                        </Typography>
                    </div>
                    <IconRight />
                </button>
                <button
                    className={[styles.addAccountButton, styles.investmentButton].join(' ')}
                    onClick={() => {
                        void NavigationService.goTo(history, '/add-accounts/property-and-assets');
                    }}
                >
                    <IconHome size="md" />
                    <div className={styles.textColumn}>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('addAccountDrawer.propertyAndAssets.buttonTitle')}
                        </Typography>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('addAccountDrawer.propertyAndAssets.buttonSubtitle')}
                        </Typography>
                    </div>
                    <IconRight />
                </button>
                <button
                    className={[styles.addAccountButton, styles.investmentButton].join(' ')}
                    onClick={() => {
                        void NavigationService.goTo(history, '/add-accounts/loans-and-debts');
                    }}
                >
                    <IconPayOverTime size="md" />
                    <div className={styles.textColumn}>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('addAccountDrawer.loansAndDebt.buttonTitle')}
                        </Typography>
                        <Typography variant={TypographyVariants.CAPTION}>
                            {LiteralsService.getLiteral('addAccountDrawer.loansAndDebt.buttonSubtitle')}
                        </Typography>
                    </div>
                    <IconRight />
                </button>
            </div>
        </Drawer>
    );
}
